import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Input, Switch } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number, tooltip } from "../../../config";
import {
  addEmailAiPrompt,
  editAcknowledgementTemplate,
  editWorkflow,
  editWorkflowEmail,
  getEmailAiPrompt,
  updateEmailAiPrompt,
  updateWorkflowFeatureSetting,
} from "../../../shared/services/workflow.services";
import { validateEmailLocalPart } from "../../../shared/validators/validator";
import { splitString } from "../../../utils";
import { copyTextToClipboard } from "../../../utils/common";
import { useWorkflowContext } from "../WorkflowContextProvider";
import { workflowQuote } from "../workflow.constants";
import { handleEditChange } from "../workflowHelper";
import "./../workflow.scss";
import { EmailRulesProvider, useEmailRules } from "./EmailRuleEngine/EmailRulesContext";
import RulesList from "./EmailRuleEngine/RulesList";
import { labels } from "./EmailRuleEngine/ruleEngine.constants";
import EmailTemplates from "./EmailTemplates";

/**
 * Header component with Add Rule button
 * @author Sarthak Arora
 */
const EmailIntegrationHeader = ({ isEmailSelected, handleEmailIntegrationSwitch, handleExpandView }) => {
  return (
    <div className='d-flex justify-content-between cursor-pointer' onClick={handleExpandView}>
      <div className='d-flex gap-1'>
        <span className='font-weight-light-semi-bold'>{label.EMAIL_INTEGRATION}</span>
      </div>
      <div className='d-flex align-items-center'>
        <span>{`${label.STATUS} ${isEmailSelected ? "" : label.DISABLED}`}</span>
        <Switch
          size="small"
          className="status-toggle"
          checked={isEmailSelected === 1}
          onChange={(e) => {
            e.nativeEvent.stopPropagation();
            handleEmailIntegrationSwitch(e);
          }}
        />
      </div>
    </div>
  );
};

/**
 * EmailIntegration component for managing email-based task creation and workflow email integration.
 *Parent Component: RightWorkflowElements
 * @author Bhavana
 * @param {number} props.featureId - The ID of the feature being displayed.
 */
const EmailIntegration = () => {
  const { workflowId, projectId } = useParams();
  const {
    workflow,
    selectedFeatures,
    tabFeatures,
    setTabFeatures,
    setSelectedFeatures,
    workflowEmailDomain,
    workflowEmails,
    expandEmail,
    setExpandEmail,
  } = useWorkflowContext();

  const [emailInputValue, setEmailInputValue] = useState("");
  const [emailLocalPart, setEmailLocalPart] = useState("");
  const [completeEmail, setCompleteEmail] = useState("");

  const [isValidEmail, setIsValidEmail] = useState(true);
  const [emailPartEdit, setEmailPartEdit] = useState(true);

  const [isEmailSelected, setIsEmailSelected] = useState(tabFeatures.find((item) => item.Id === number.EIGHT)?.IsSelected);

  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  /**
   * sets the initial email input value
   * @author {Sarthak Arora}
   */
  useEffect(() => {
    const emailName = splitString(workflowEmails?.[number.ZERO]?.EmailId, "@");
    setEmailLocalPart(emailName?.[number.ZERO]);
    setEmailInputValue(emailName?.[number.ZERO]);
  }, [workflowEmails]);

  /**
   * sets the default email
   * @author { Sejal Bansal}
   */
  useEffect(() => {
    setCompleteEmail(`${emailInputValue}@${workflowEmailDomain}`);
  }, [emailInputValue, workflowEmailDomain]);

  /**
   * copies the email to clipboard
   * @author {Sarthak Arora}
   */

  const copyToClipboard = useCallback(() => {
    if (isValidEmail) {
      const email = `${emailInputValue}@${workflowEmailDomain}`;
      copyTextToClipboard(email, label.EMAIL_COPIED);
    }
  }, [emailInputValue, workflowEmailDomain]);

  /**
   * handles the input of email's local part also checks for validation
   * @author {Sarthak Arora}
   */
  const handleEmailInput = (e) => {
    const lowerCaseValue = e.target.value.toLowerCase();
    setEmailInputValue(lowerCaseValue);
    if (validateEmailLocalPart(lowerCaseValue)) {
      setIsValidEmail(true);
    } else setIsValidEmail(false);
  };

  /**
   * updates the workflow email if the local part of the email changes
   * @author {Sarthak Arora}
   */
  const updateWorkflowEmail = () => {
    setEmailPartEdit(true);
    if (emailInputValue !== emailLocalPart && isValidEmail && workflow?.Id) {
      dispatch(editWorkflowEmail({ workflowEmailId: workflowEmails?.[number.ZERO]?.Id, emailInfo: emailInputValue }));
      setEmailLocalPart(emailInputValue);
      setEmailPartEdit(true);
    }
  };

  const handleEditEmail = () => {
    setEmailPartEdit(false);
  };

  /**
   * Toggles the expanded view for the email integration section.
   * @author Bhavana
   */
  const handleExpandView = () => {
    setExpandEmail(true);
  };

  /**
   * Handles the email integration switch change
   * @author {Sarthak Arora}
   */
  const handleEmailIntegrationSwitch = (event) => {
    const isEnabled = event.target.value;
    const payload = {
      workflowId: workflowId,
      featureId: number.EIGHT,
      isSelected: isEnabled ? 1 : 0,
      userId: user.id,
    };
    dispatch(updateWorkflowFeatureSetting(payload));
    setTabFeatures(
      tabFeatures.map((item) => {
        if (item.Id === number.EIGHT) item.IsSelected = isEnabled ? 1 : 0;
        return item;
      })
    );
    setIsEmailSelected(isEnabled ? 1 : 0);
  };

  return (
    <>
      <EmailRulesProvider>
        <div className='mt-3'>
          <EmailIntegrationHeader
            isEmailSelected={isEmailSelected}
            handleEmailIntegrationSwitch={handleEmailIntegrationSwitch}
            handleExpandView={handleExpandView}
          />
          {workflowId && (
            <div className='mt-2 justify-content-start '>
              <div className='row'>
                <div className='col-lg-6 col-md-6 col-sm-12 border-right workflow-email-left-section'>
                  <Tooltip parentTitle={true} position='bottom'>
                    <div className='workflow-email-details '>
                      <label className='text-black'>Email</label>
                      <div className='d-flex align-items-center email-wrapper-div'>
                        {emailPartEdit && (
                          <span title={completeEmail} className='text-truncate'>
                            {completeEmail}
                          </span>
                        )}
                        {!emailPartEdit && (
                          <div className='email-editing-mode d-flex'>
                            <Input
                              className='email-based-task-email-input border-none'
                              id='workflow-email-based-task-email-input'
                              disabled={emailPartEdit}
                              value={emailInputValue}
                              onChange={handleEmailInput}
                              onBlur={updateWorkflowEmail}
                              maxLength={number.HUNDRED}></Input>
                            <div className='d-flex align-items-center'>
                              <Input
                                id='workflow-email-based-task-domain-input'
                                className='email-based-task-domain-input mr-2 border-none px-0'
                                disabled={true}
                                defaultValue={`@${workflowEmailDomain}`}
                                value={`@${workflowEmailDomain}`}></Input>
                              <div className='d-flex'>
                                <span
                                  title='Save Email'
                                  className='cursor-pointer edit-template-icon  align-items-center ml-2'
                                  onClick={updateWorkflowEmail}
                                  id='email-edit-icon'>
                                  {icon.DONE}
                                </span>
                                <span
                                  id='workflow-copy-email-btn'
                                  title={tooltip.COPY_EMAIL}
                                  className={`cursor-pointer align-items-center ml-2 mr-1 edit-template-icon ${
                                    !isValidEmail ? "disabled-copy-btn" : ""
                                  }`}
                                  onClick={copyToClipboard}>
                                  {icon.COPY_CONTENT}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {emailPartEdit && (
                          <span
                            title='Edit Email'
                            className='cursor-pointer align-items-center ml-2 edit-template-icon'
                            onClick={handleEditEmail}
                            id='email-edit-icon'>
                            {icon.EDIT_ICON}
                          </span>
                        )}
                        {emailPartEdit && (
                          <span
                            id='workflow-copy-email-btn'
                            title={tooltip.COPY_EMAIL}
                            className={`cursor-pointer align-items-center ml-2 edit-template-icon ${!isValidEmail ? "disabled-copy-btn" : ""}`}
                            onClick={copyToClipboard}>
                            {icon.COPY_CONTENT}
                          </span>
                        )}
                      </div>
                      {!isValidEmail && (
                        <div className='justify-content-center invalid-email-warning'>
                          <p className=' warning-text text-red mr-1 mt-1'> {label.INVALID_EMAIL}</p>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                  <EmailTemplates />

                  {isEmailSelected === 1 && (
                    <div className='information-panel d-flex panel-default mt-2'>
                      <span className='panel-icon d-flex align-items-center'>{icon.TRIANGLE_ALERT}</span>
                      <div className='panel-body align-items-center'>
                        <div className='panel-message cursor-default'>{workflowQuote.EMAIL_INTEGRATIONS}</div>
                      </div>
                    </div>
                  )}
                </div>
                <RulesList />
              </div>
            </div>
          )}
        </div>
      </EmailRulesProvider>
    </>
  );
};
export default EmailIntegration;
